// Utility to fix missing user documents in Firestore
// This happens when users exist in Firebase Auth but not in Firestore

import { doc, setDoc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

export const createMissingUserDocument = async (user, role = 'host') => {
  try {
    // Check if user document exists
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // Create the missing user document
      await setDoc(userDocRef, {
        fullName: user.displayName || user.email.split('@')[0],
        email: user.email,
        role: role,
        plan: 'host', // Default plan for all users
        accountStatus: 'verified', // Required by Firestore rules
        emailVerified: user.emailVerified || false,
        emailVerifiedAt: user.emailVerified ? new Date() : null,
        createdAt: new Date(),
        // Remove team-related fields if this is a host/admin
        teamId: null,
        leagueId: null
      });
      
      console.log('✅ Created missing user document for:', user.email);
      return true;
    } else {
      console.log('ℹ️ User document already exists for:', user.email);
      return false;
    }
  } catch (error) {
    console.error('❌ Error creating user document:', error);
    return false;
  }
};

// Function to fix all missing user documents
export const fixAllMissingUserDocuments = async (auth) => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      await createMissingUserDocument(currentUser, 'host'); // Default to host, not admin
    }
  } catch (error) {
    console.error('❌ Error fixing user documents:', error);
  }
};

// Function to fix missing accountStatus field for existing users
export const fixMissingAccountStatus = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (!userData.accountStatus) {
        await updateDoc(userRef, {
          accountStatus: 'verified'
        });
        console.log('✅ Fixed missing accountStatus for user:', userId);
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('❌ Error fixing accountStatus:', error);
    return false;
  }
};

// Migration function to add plan field to existing users
export const migrateExistingUsersToPlanSystem = async () => {
  try {
    console.log('🔄 Starting migration to add plan field to existing users...');
    
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    let migratedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;
      
      try {
        // Skip if already has plan field
        if (userData.plan !== undefined) {
          skippedCount++;
          continue;
        }
        
        // Skip admin accounts - don't modify them
        if (userData.role === 'admin') {
          console.log(`⏭️ Skipping admin account: ${userData.email}`);
          skippedCount++;
          continue;
        }
        
        // Skip team/player accounts - leave them alone
        if (userData.role === 'team_captain' || userData.role === 'team_member') {
          console.log(`⏭️ Skipping team account: ${userData.email}`);
          skippedCount++;
          continue;
        }
        
        // Add plan field to regular users
        await updateDoc(doc(db, 'users', userId), {
          plan: 'host',
          migratedAt: new Date()
        });
        
        migratedCount++;
        console.log(`✅ Migrated user to host plan: ${userData.email}`);
      } catch (error) {
        errorCount++;
        console.error(`❌ Failed to migrate user ${userData.email}:`, error);
      }
    }
    
    console.log(`Migration complete: ${migratedCount} users migrated, ${skippedCount} skipped, ${errorCount} errors`);
    
    return {
      success: true,
      migratedCount,
      skippedCount,
      errorCount,
      message: `Successfully migrated ${migratedCount} users to plan system`
    };
  } catch (error) {
    console.error('Error during migration:', error);
    return {
      success: false,
      message: error.message
    };
  }
}; 